import { SafeError, SafePromise, SafePromiseMultiError, safeAwait, safeAwaitAll } from './safeAwait';

export type SafeTimeoutErrorData = {
  fetchDurationMs: number;
  timeoutBudgetMs: number;
};

function createTimeoutError(startedAtMs: number, timeoutBudgetMs: number): SafeError<'timeout', SafeTimeoutErrorData> {
  const fetchDurationMs = Date.now() - startedAtMs;
  return new SafeError(`the promise has not finished in the requested time (${fetchDurationMs}ms)`, 'timeout', {
    fetchDurationMs,
    timeoutBudgetMs,
  });
}

export const safeTimeout = <T>(promise: SafePromise<T>, timeoutInMilliseconds: number): SafePromise<T> => {
  const startedAtMs = Date.now();
  return Promise.race([
    promise,
    safeAwait<T>(
      new Promise((_, reject) =>
        setTimeout(() => reject(createTimeoutError(startedAtMs, timeoutInMilliseconds)), timeoutInMilliseconds),
      ),
    ),
  ]);
};

export const safeTimeoutAll = <T>(
  promise: SafePromiseMultiError<T>,
  timeoutInMilliseconds: number,
): SafePromiseMultiError<T> => {
  const startedAtMs = Date.now();
  const timeout = safeAwaitAll([
    new Promise((_, reject) =>
      setTimeout(() => reject({ err: createTimeoutError(startedAtMs, timeoutInMilliseconds) }), timeoutInMilliseconds),
    ),
  ]);
  return Promise.race([promise, timeout as SafePromiseMultiError<T>]);
};

export const safeDelay = (timeoutInMilliseconds: number): SafePromise<undefined> => {
  return safeAwait(new Promise(response => setTimeout(() => response(undefined), timeoutInMilliseconds)));
};
